import React, { useEffect, useRef } from "react";
import { Button, Container, Divider, Group, MultiSelect, Paper, ScrollArea, Space, Stack, Text, TextInput, Textarea, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconChevronLeft, IconLock, IconTrash, IconUser } from "@tabler/icons-react";

import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../RoleTemplate";
import { useAccess } from "../../Auth/auth-functions";
import { useGetTeamsQuery } from "../../api/team.api";
import { LocationDto, MutableLocationDto } from "../../models/location.models";
import classes from "./LocationDetailView.module.css";

interface LocationEditViewProps {
	location?: LocationDto | null;
	onSave: (values: MutableLocationDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	// isChanged?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const LocationEditView: React.FC<LocationEditViewProps> = ({
	location,
	onSave,
	onClose,
	isSaving = false,
	// isChanged = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { data: teams } = useGetTeamsQuery();
	const teamItems = teams?.map(team => ({ value: team.TeamId, label: team.Name })) || [];

	const isNew = !location || location.LocationId === "new";
	const canEditLocation = useAccess(AzureADRoles.Admin);

	const form = useForm<MutableLocationDto>({
		initialValues: {
			LocationId: location?.LocationId || "",
			Title: location?.Title || "",
			Description: location?.Description || "",
			Address: location?.Address || "",
			Teams: location?.Teams.map(t => t.TeamId) || [],
		},
		validate: {
			Title: (value) => (value.length < 3 ? "Title must be at least 3 characters long" : null),
			Teams: (value) => (value.length === 0 ? "At least one team must be selected" : null),
		},
	});

	// Track dirty state and call onChanged
	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty() && canEditLocation) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		form.setValues({
			Title: location?.Title || "",
			Address: location?.Address || "",
			Description: location?.Description || "",
			Teams: location?.Teams.map(t => t.TeamId) || []
		});
		// eslint-disable-next-line
	}, [location]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};


	if (location?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Location Deleted
					</Text>
					<Text ta="center">This location has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	const noAccess = (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					No Access
				</Text>
				<Text ta="center">You do not have permission to create or edit locations. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea  type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{location ? "Edit Location" : "New Location"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20">
					<form onSubmit={form.onSubmit((values) => handleSave(values))}>
						<Button type="submit" style={{ display: 'none' }} />
						<TextInput
							label="Location Name"
							key={form.key("Title")}
							{...form.getInputProps("Title")}
							placeholder="Enter location title"
							maxLength={255}
							required
							mb="xl"
						/>

						<TextInput
							label="Address"
							key={form.key("Address")}
							{...form.getInputProps("Address")}
							placeholder="Enter location address"
							maxLength={255}
							mb="xl"
						/>

						<Textarea
							label="Description"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							placeholder="Enter location description"
							mb="xl"
							autosize
							minRows={3}
						/>

						<MultiSelect label="Teams in Charge"
							key={form.key('Teams')}
							data={teamItems}
							{...form.getInputProps('Teams')}
							placeholder="Select teams"
							checkIconPosition="right"
							required
							searchable
							leftSection={<IconUser size={16} />}
							mb="xl"
						/>
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>
};

export default LocationEditView;
